import { BuildVersion, type BuildInfo } from './build-version';
import { BRAND } from './brand';
import { Link } from 'react-router-dom';
import { SITE } from './site-config';
import React, { type CSSProperties, type ReactNode } from 'react';
import { initLandingMotion } from './landing-motion';
import { MEMORIES, WALL_COLUMNS, memorySources, type MemoryId } from './landing-memories';

function StudioLink({
  url,
  className,
  children,
}: {
  url: string;
  className: string;
  children: ReactNode;
}) {
  return url.startsWith('/') ? (
    <Link className={className} to={url}>
      {children}
    </Link>
  ) : (
    <a className={className} href={url}>
      {children}
    </a>
  );
}

function Arrow({ diagonal = false }: { diagonal?: boolean }) {
  return (
    <svg width="18" height="18" viewBox="0 0 24 24" fill="none" aria-hidden="true">
      <path
        d={diagonal ? 'M6 18 18 6M6 6h12v12' : 'M4 12h16m-6-6 6 6-6 6'}
        stroke="currentColor"
        strokeWidth="1.6"
        strokeLinecap="round"
        strokeLinejoin="round"
      />
    </svg>
  );
}

function MemoryPhoto({
  id,
  wall = false,
  portrait = false,
  tilt = 0,
  order = 0,
  sizes,
}: {
  id: MemoryId;
  wall?: boolean;
  portrait?: boolean;
  tilt?: number;
  order?: number;
  sizes?: string;
}) {
  const photo = MEMORIES[id];
  const sources = memorySources(id);
  const imageSizes = sizes || '(max-width: 480px) 30vw, (max-width: 960px) 22vw, 190px';
  return (
    <figure
      className={`home-memory${wall ? ' home-wall-photo' : ''}${portrait ? ' home-memory-portrait' : ''}`}
      data-wall-photo={wall ? '' : undefined}
      data-order={wall ? order : undefined}
      style={
        {
          '--tilt': `${wall ? tilt * 0.35 : 0}deg`,
          '--arrival-delay': `${order * 90}ms`,
          '--mobile-order': order,
        } as CSSProperties
      }
    >
      <div className="home-memory-image">
        <picture>
          <source type="image/avif" srcSet={sources.avif} sizes={imageSizes} />
          <img
            src={sources.src}
            srcSet={sources.srcSet}
            sizes={imageSizes}
            width="1536"
            height="1024"
            alt={photo.alt}
            loading={wall && order < 8 ? 'eager' : 'lazy'}
            fetchPriority={wall && order === 0 ? 'high' : undefined}
            decoding="async"
            data-memory-image
          />
        </picture>
        <span className="home-photo-fallback" aria-hidden="true">
          <img src="/brand/symbol.svg" width="32" height="32" alt="" />
          {photo.label}
        </span>
      </div>
      <figcaption>{photo.label}</figcaption>
    </figure>
  );
}
const travelStories: { id: MemoryId; category: string; title: string; text: string }[] = [
  {
    id: 'mountain-solo',
    category: '一个人的远方',
    title: '一个人，也值得被好好记录',
    text: '回头时的笑，刚好遇见这一程的光。',
  },
  {
    id: 'river-couple',
    category: '两个人的旅途',
    title: '风景很远，喜欢的人就在身边',
    text: '牵着手走过的地方，都有了特别的意义。',
  },
  {
    id: 'lake-partners',
    category: '长久的陪伴',
    title: '走过很多地方，身边还是你',
    text: '在新的风景里，留下熟悉的笑容。',
  },
  {
    id: 'sea-family',
    category: '一家人的时光',
    title: '这一次，全家都在照片里',
    text: '一起出发的笑容，就留在同一张照片里。',
  },
];
const steps = [
  { title: '找到这一程', text: '按拍摄地点和日期查找相册，或从摄影师分享的入口进入。' },
  { title: '看看镜头里的你', text: '浏览已经发布的照片，看看效果，挑选喜欢的画面。' },
  { title: '把喜欢的带回家', text: '在小程序选购照片，在有效下载期内保存原片，珍藏这一程。' },
];
export function Landing({
  studioUrl = '/login',
  buildInfo,
}: {
  studioUrl?: string;
  buildInfo?: BuildInfo;
}) {
  const miniProgramReady = Boolean(SITE.miniProgramCode || SITE.miniProgramUrl);
  React.useEffect(() => initLandingMotion(document.querySelector('.home')), []);

  return (
    <div className="home" id="top">
      <a className="skip-link" href="#home-main">
        跳到主要内容
      </a>
      <header className="home-header home-header-solid">
        <div className="home-container home-header-inner">
          <Link to="/" className="brand" aria-label={`${BRAND.name} ${BRAND.latinName} 首页`}>
            <img
              src="/brand/wordmark-white.svg"
              width="224"
              height="80"
              alt={`${BRAND.name} ${BRAND.latinName}`}
            />
          </Link>
          <nav className="home-nav" aria-label="官网导航">
            <a className="home-nav-about" href="#travel">
              旅行留念
            </a>
            <a className="home-nav-about" href="#motion">
              运动瞬间
            </a>
            <a className="home-nav-about" href="#how-it-works">
              如何找图
            </a>
            {studioUrl ? (
              <StudioLink className="home-studio-link" url={studioUrl}>
                摄影师工作台 <Arrow diagonal />
              </StudioLink>
            ) : (
              <a className="home-studio-link" href="#creators">
                摄影师专区 <Arrow diagonal />
              </a>
            )}
          </nav>
        </div>
      </header>

      <main id="home-main" className="home-main">
        <section className="home-hero" aria-labelledby="home-title">
          <span className="home-hero-mark" aria-hidden="true">
            ZHUIJIAO
          </span>
          <div className="home-container home-hero-grid">
            <div className="home-hero-copy">
              <p className="home-eyebrow">EVERY JOURNEY, YOUR STORY.</p>
              <h1 id="home-title">
                <span className="home-line">
                  <span className="home-line-inner">不经意的瞬间，</span>
                </span>
                <span className="home-line">
                  <span className="home-line-inner">
                    值得<span className="home-accent">好好珍藏</span>
                  </span>
                </span>
              </h1>
              <p className="home-intro">
                天南海北，走过的路，
                <br />
                都有你的故事。
              </p>
              <div className="home-hero-actions">
                <a
                  className="home-button"
                  href={miniProgramReady ? '#miniprogram' : '#how-it-works'}
                >
                  {miniProgramReady ? '找我的照片' : '了解如何找照片'} <Arrow />
                </a>
                <a className="home-quiet-link" href="#creators">
                  摄影师入驻 <Arrow diagonal />
                </a>
              </div>
              <p className="home-hero-note">旅行与运动影像 · 找图 / 选购 / 原片交付</p>
              <div className="home-hero-tags" aria-label="探索旅行与运动场景">
                <a href="#travel">山河湖海</a>
                <a href="#travel">独行与相伴</a>
                <a href="#travel">全家出游</a>
                <a href="#motion">骑行与奔跑</a>
              </div>
            </div>
            <div className="home-hero-side" aria-hidden="true">
              <span className="home-hero-vertical">追焦 · 一路有你</span>
              <span className="home-hero-rule" />
            </div>
            <div className="home-visual">
              <div className="home-wall-frame">
                <span className="home-corner home-corner-tl" aria-hidden="true" />
                <span className="home-corner home-corner-tr" aria-hidden="true" />
                <span className="home-corner home-corner-bl" aria-hidden="true" />
                <span className="home-corner home-corner-br" aria-hidden="true" />
                <div className="home-wall" aria-label="旅行与运动照片墙" data-photo-wall>
                  {WALL_COLUMNS.map((column, index) => (
                    <div className={`home-wall-column home-wall-column-${index + 1}`} key={index}>
                      {column.map((photo) => (
                        <MemoryPhoto
                          key={photo.id}
                          {...photo}
                          wall
                          sizes="(max-width: 760px) 29vw, (max-width: 1100px) 12vw, 140px"
                        />
                      ))}
                    </div>
                  ))}
                </div>
              </div>
              <div className="home-visual-caption">
                <span className="home-exif">THE PLACES. THE PEOPLE. THE MOMENTS.</span>
                <span>山河湖海 · 一路有你</span>
              </div>
            </div>
          </div>
          <a className="home-scroll-cue" href="#how-it-works" aria-label="向下滚动查看介绍">
            <span>SCROLL</span>
            <i aria-hidden="true" />
          </a>
        </section>

        <div className="home-marquee" aria-hidden="true">
          <div className="home-marquee-track">
            {[0, 1].map((group) => (
              <div
                className="home-marquee-group"
                key={group}
                aria-hidden={group === 1 || undefined}
              >
                {[
                  '天南海北',
                  'EVERY JOURNEY, YOUR STORY',
                  '山河湖海',
                  '旅行留念',
                  '骑行与奔跑',
                ].map((word) => (
                  <React.Fragment key={word}>
                    <span className="home-marquee-word">{word}</span>
                    <span className="home-marquee-slash">/</span>
                  </React.Fragment>
                ))}
              </div>
            ))}
          </div>
        </div>

        <section
          className="home-travel home-container home-section"
          id="travel"
          aria-labelledby="travel-title"
        >
          <div className="home-section-heading" data-reveal>
            <div>
              <p className="home-eyebrow">01 / 旅行留念</p>
              <h2 id="travel-title">
                把风景里的你，
                <br />
                也一起留下
              </h2>
            </div>
            <p className="home-section-intro">
              一个人看世界，两个人走远路。
              <br />
              和喜欢的人一起，留下喜欢的自己。
            </p>
          </div>
          <div className="home-travel-stories">
            {travelStories.map((story, index) => (
              <article
                className="home-travel-story"
                key={story.id}
                data-reveal
                style={{ '--reveal-delay': `${index * 90}ms` } as CSSProperties}
              >
                <MemoryPhoto
                  id={story.id}
                  tilt={[-2, 2, -1, 2][index]}
                  sizes="(max-width: 960px) 44vw, 280px"
                />
                <p className="home-story-category">{story.category}</p>
                <h3>{story.title}</h3>
                <p>{story.text}</p>
              </article>
            ))}
          </div>
        </section>
        <section className="home-motion" id="motion" aria-labelledby="motion-title">
          <div className="home-container home-motion-layout">
            <div className="home-motion-copy" data-reveal>
              <p className="home-eyebrow">02 / 运动瞬间</p>
              <h2 id="motion-title">
                迎着风的你，
                <br />
                也值得被珍藏
              </h2>
              <p>
                骑过的山路，跑过的湖岸，
                <br />
                都是这一程鲜活的自己。
              </p>
              <a className="home-text-link" href="#how-it-works">
                找回运动中的瞬间 <Arrow />
              </a>
              <p className="home-motion-types">骑行 / 跑步 / 越野 / 马拉松 / 摩托</p>
            </div>
            <div
              className="home-motion-photos"
              data-reveal
              style={{ '--reveal-delay': '120ms' } as CSSProperties}
            >
              <MemoryPhoto
                id="cycling"
                sizes="(max-width: 760px) 82vw, (max-width: 1100px) 44vw, 560px"
              />
              <MemoryPhoto
                id="trail-running"
                sizes="(max-width: 760px) 82vw, (max-width: 1100px) 44vw, 560px"
              />
            </div>
          </div>
        </section>
        <section className="home-how home-container" id="how-it-works" aria-labelledby="how-title">
          <div className="home-section-heading" data-reveal>
            <p className="home-eyebrow">03 / 找到你的照片</p>
            <h2 id="how-title">
              把这一程，
              <br />
              带回相册
            </h2>
          </div>
          <ol className="home-steps" role="list">
            {steps.map((step, i) => (
              <li
                key={step.title}
                data-reveal
                style={{ '--reveal-delay': `${i * 110}ms` } as CSSProperties}
              >
                <span className="home-step-number" aria-hidden="true">
                  0{i + 1}
                </span>
                <h3>{step.title}</h3>
                <p>{step.text}</p>
              </li>
            ))}
          </ol>
        </section>

        <section className="home-statement" aria-label="追焦理念">
          <span className="home-statement-mark" aria-hidden="true">
            MOMENT
          </span>
          <div className="home-container" data-reveal>
            <p className="home-statement-en">EVERY JOURNEY, YOUR STORY</p>
            <p className="home-statement-line">
              天南海北，
              <br />
              <span className="home-statement-accent">都有你的故事</span>
            </p>
          </div>
        </section>

        <section className="home-connect home-container" aria-label="加入追焦">
          <div className="home-mini" id="miniprogram" data-reveal>
            <div className="home-mini-copy">
              <p className="home-eyebrow">追焦 · 微信小程序</p>
              <h2>{miniProgramReady ? '去找镜头里的自己' : '下一程，在追焦相遇'}</h2>
              <p>
                {miniProgramReady
                  ? '在微信里找图、购买，保存属于你的瞬间。'
                  : '小程序即将上线，下一程见。'}
              </p>
              {SITE.miniProgramUrl && (
                <a
                  className="home-mini-link"
                  href={SITE.miniProgramUrl}
                  target="_blank"
                  rel="noopener noreferrer"
                >
                  在微信打开 <Arrow diagonal />
                </a>
              )}
            </div>
            {SITE.miniProgramCode ? (
              <figure className="home-mini-code">
                <img
                  src={SITE.miniProgramCode}
                  width="144"
                  height="144"
                  alt="追焦官方小程序码，使用微信扫描进入"
                  loading="lazy"
                />
                <figcaption>微信扫码进入</figcaption>
              </figure>
            ) : (
              <div className="home-mini-soon">
                <img src="/brand/symbol-white.svg" width="72" height="72" alt="" loading="lazy" />
                <span>{miniProgramReady ? '在微信里相遇' : '即将上线'}</span>
              </div>
            )}
          </div>
          <div className="home-creator" id="creators" data-reveal>
            <p className="home-eyebrow">摄影师专区</p>
            <h2>
              你专注快门，
              <br />
              我们把作品送达
            </h2>
            <p>上传、发布和管理作品，让被你记录的人，更容易找到照片。</p>
            {studioUrl ? (
              <StudioLink className="home-text-link" url={studioUrl}>
                进入摄影师工作台 <Arrow diagonal />
              </StudioLink>
            ) : (
              <p className="home-creator-status">摄影师工作台即将开放</p>
            )}
          </div>
        </section>
      </main>

      <footer className="home-footer">
        <div className="home-container">
          <div className="home-footer-top">
            <p className="home-footer-slogan">
              {BRAND.slogan}
              <span> {BRAND.englishSlogan}</span>
            </p>
            <img
              src="/brand/symbol-white.svg"
              width="40"
              height="40"
              alt=""
              loading="lazy"
              aria-hidden="true"
            />
          </div>
          <div className="home-footer-details">
            <span>
              © {new Date().getFullYear()} {SITE.operator || BRAND.latinName}
            </span>
            {SITE.url && <a href={SITE.url}>{new URL(SITE.url).hostname}</a>}
            {SITE.icpNumber ? (
              <a href="https://beian.miit.gov.cn/" target="_blank" rel="noopener noreferrer">
                {SITE.icpNumber}
              </a>
            ) : (
              <span className="home-filing-placeholder">ICP备案号：待填写</span>
            )}
            {SITE.publicSecurityNumber &&
              (SITE.publicSecurityUrl ? (
                <a href={SITE.publicSecurityUrl} target="_blank" rel="noopener noreferrer">
                  {SITE.publicSecurityNumber}
                </a>
              ) : (
                <span>{SITE.publicSecurityNumber}</span>
              ))}
            <span className="home-image-credit">页面情境图片由 AI 生成</span>
            <BuildVersion info={buildInfo} />
          </div>
        </div>
      </footer>
    </div>
  );
}
